import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { LABELS } from "./scientific-archive-folder.constant";

import { ScientificArchiveFolderDto } from "src/app/models/scientific-archive-folder.model";
import { EMPTY, Observable, Subject, Subscription, catchError, filter, switchMap, takeUntil, tap } from "rxjs";
import { ApiService } from "src/app/services/api.service";
import { RoutingService } from "src/app/services/routing.service";
import { MessageService } from "primeng/api";
import { EntityType } from "src/app/enums/entity-type.enum";
import { ArchiveMapInformationDto } from "src/app/models/scientific-archive-document-dto";
import { HttpResponse } from "@angular/common/http";
import { eDetailsView } from "../shared/components/tablet-view-tabs/details-veiw.enum";
import { LanguageService } from "src/app/services/language.service";
import { GaService } from "src/app/services/ga.service";
import { ARCGIS_MAP_LIFECYCLE } from "../arcgis-map/constants/map-life-cycle.interface";
import { MainMapService } from "../arcgis-map/services/main-map.service";
import { MapControlItem } from "../arcgis-map/constants/map-control-item.interface";
import { ControlType } from "../arcgis-map/constants/control-type.enum";

@Component({
    selector: 'app-scientific-archive-folder',
    templateUrl: './scientific-archive-folder.component.html',
    styleUrls: ['./scientific-archive-folder.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [MessageService,
        {			
        provide: ARCGIS_MAP_LIFECYCLE,
        useClass: MainMapService
        }
	]
})
export class ScientificArchiveFolderComponent implements OnInit {

    @Input() closeClicked!: Subject<void>;
    @Input() currentLanguage!: Observable<string>;
    archiveFolder?: ScientificArchiveFolderDto;
    detailsView: eDetailsView = eDetailsView.Info;
    entityId: number = -1;
    shapes: any[] = [];
    zoomShapeIds: string[] = [];

    eDetailsView = eDetailsView;
    LABELS = LABELS;
    errorMessage = '';
    hasError = false;

    private entityFolderSubscription?: Subscription;
    currentLanguage$!: Observable<string>;
 	mapControls: MapControlItem[] = [
		{ position: 'top-left', type: ControlType.FullScreen },
		{ position: 'top-left', type: ControlType.Zoom },
		{
			position: 'top-left',
			type: ControlType.BasemapGallery,
			expand: {
				id: 'basemap-gallery-expand',
				position: 'top-left',
				expandIcon: 'basemap',
				expandTooltip: '',
				mode: 'floating',
			},
		},

	];

    constructor(
        private api: ApiService,
        private routingService: RoutingService,
        private messageService: MessageService,
        private cd: ChangeDetectorRef,
        private languageService: LanguageService,
        private gaService:GaService

    ){}

    ngOnInit(): void {
        this.currentLanguage$ = this.languageService.currentLanguage$;
        this.archiveFolder = undefined;
        this.hasError = false;
        this.entityFolderSubscription = this.routingService.entityDetails$.pipe(
            filter((entityDetails) => {
                return !!entityDetails.id && [EntityType.SCIENTIFIC_ARCHIVE_FOLDERS].includes(entityDetails.type as EntityType);
            }),
            tap((entityDetails) => {
                const { id } = entityDetails;
                this.entityId = id;
            }),
            switchMap((entityDetails) => {
                return this.api.getArchiveFolder(entityDetails.id).pipe(
                    takeUntil(this.closeClicked),
                    catchError((error) => {
                        this.hasError = true;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: error.message || 'General error'
                        });
                        return EMPTY;
                    }));
            })
        )
            .subscribe((data) => {
                if (data == null) return;
                this.archiveFolder = data;
                if (data.archiveMapInformation !== undefined && data.archiveMapInformation.length > 0) {
                    this.shapes = data.archiveMapInformation.map((i) => this.convertToConvertedShape(i));
                    this.zoomShapeIds = data.archiveMapInformation.map(i => `${i.shapeId}`);
                }
                this.cd.markForCheck();
            });

    }

    private convertToConvertedShape(shape: ArchiveMapInformationDto): any {
        const entity = shape.shapeData.entity;
        return {
            shapeId: shape.shapeId.toString(),
            wkt: shape.shapeWkt,
            layerName: "שכבת תוצאות שאילתה",
            hidden: false,
            isExtent: true,
            shapeData: {
                entity: {
                    entityId: entity.entityId,
                    entityType: entity.entityType
                },
            },
            styledParams: {
                geometryType: "Polygon", // Assuming default geometry type is Polygon
                fillColor: [255, 165, 0, 0.2],
                borderColor: [255, 165, 0],
                borderWidth: 2,
            },
        };
    }

    OnViewFolder(entityId: number) {
        this.gaService.trackWithGA(EntityType.SCIENTIFIC_ARCHIVE_FOLDERS, 'entityType')
        const queryParams = {
            "entityId": entityId,
            "entityType": EntityType.SCIENTIFIC_ARCHIVE_FOLDERS
        }
        this.routingService.navigateToChild(queryParams, 'folders:' + entityId);
    }

    OnViewDocument(entityId: number) {
        this.gaService.trackWithGA(EntityType.SCIENTIFIC_ARCHIVE_DOCUMENTS, 'entityType')
        const queryParams = {
            "entityId": entityId,
            "entityType": EntityType.SCIENTIFIC_ARCHIVE_DOCUMENTS
        }
        this.routingService.navigateToChild(queryParams, 'documents:' + entityId);
    }

    ngOnDestroy(): void {
        this.entityFolderSubscription?.unsubscribe();
    }

    onViewChanged($event: eDetailsView) {
        this.detailsView = $event;
    }

    handleZip() {
        this.errorMessage = '';
        this.downloadFile(this.archiveFolder?.folderLongId || 'no_name')
    }

    private downloadFile(filename: string) {
        const link = document.createElement('a');
        link.href = this.archiveFolder?.folderDownloadUrl || '';
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

}